<template>
	<JFlex class="s-justify-between s-item-stretch">
		<JFlex class="s-items-center s-py-1">
			<JFlex class="s-rounded s-text-gray-900">
				<button
					class="s-appearance-none s-pl-4 s-pr-2 s-py-2 focus:s-outline-none hover:s-bg-indigo-100"
					@click="$emit('loadBuckets')"
				>
					{{ $t("root") }}
				</button>
				<button
					class="s-flex s-appearance-none hover:s-bg-indigo-100 s-items-center focus:s-outline-none"
					@click="onPathClick(null)"
				>
					<div class="s-px-1 s-py-2 s-font-bold">></div>
					<div class=" s-px-1 s-py-2">{{ bucketName }}</div>
				</button>
				<button
					v-for="(p, pi) in path"
					:key="pi"
					class="s-flex s-appearance-none hover:s-bg-indigo-100 s-items-center focus:s-outline-none"
					@click="onPathClick(p)"
				>
					<div class="s-px-1 s-py-2 s-font-bold">></div>
					<div class=" s-px-1 s-py-2">{{ p.name }}</div>
				</button>
			</JFlex>
		</JFlex>

		<!-- Top level actions -->
		<div class="s-flex s-item-stretch">
			<!-- Refresh -->
			<button
				class="s-flex s-justify-center s-items-center s-px-3 s-appearance-none focus:s-outline-none s-text-gray-900 s-border-l s-border-700 hover:s-bg-indigo-100"
				title="Refresh"
				@click="onRefresh"
			>
				<svg
					width="18"
					height="18"
					class="s-text-current"
					fill="none"
					stroke-linecap="round"
					stroke-linejoin="round"
					stroke-width="2"
					viewBox="0 0 24 24"
					stroke="currentColor"
				>
					<path
						d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
					></path>
				</svg>
			</button>
			<!-- Toggle Search -->
			<button
				class="s-flex s-justify-center s-items-center s-px-3 s-appearance-none focus:s-outline-none s-text-gray-900 s-border-l s-border-700 hover:s-bg-indigo-100"
				title="Search"
				:class="{ 's-bg-indigo-100': isSearching === true }"
				@click="onSearch"
			>
				<svg
					width="18"
					height="18"
					class="s-text-current"
					fill="none"
					stroke-linecap="round"
					stroke-linejoin="round"
					stroke-width="2"
					viewBox="0 0 24 24"
					stroke="currentColor"
				>
					<path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
				</svg>
			</button>
			<!-- Create Folder -->
			<button
				class="s-flex s-justify-center s-items-center s-px-3 s-appearance-none focus:s-outline-none s-text-gray-900 s-border-l s-border-700 hover:s-bg-indigo-100"
				title="Create Folder"
				@click="onCreateFolder"
			>
				<svg
					width="18"
					height="18"
					class="s-text-current"
					fill="none"
					stroke-linecap="round"
					stroke-linejoin="round"
					stroke-width="2"
					viewBox="0 0 24 24"
					stroke="currentColor"
				>
					<path d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"></path>
				</svg>
			</button>
			<!-- Paste -->
			<button
				class="s-flex s-justify-center s-items-center s-px-3 s-appearance-none focus:s-outline-none s-text-gray-400 s-border-l s-border-700"
				:class="[{ 'opacity-50 pointer-events-none': !hasCutItem }, { 's-text-gray-900 hover:s-bg-indigo-100': hasCutItem }]"
				title="Paste"
				@click="onPaste"
			>
				<svg
					width="18"
					height="18"
					class="s-text-current"
					fill="none"
					stroke-linecap="round"
					stroke-linejoin="round"
					stroke-width="2"
					viewBox="0 0 24 24"
					stroke="currentColor"
				>
					<path
						d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
					></path>
				</svg>
			</button>
			<button
				class="fs-flex s-justify-center s-items-center s-px-3 s-appearance-none focus:s-outline-none s-text-gray-900 s-border-l s-border-700 hover:s-bg-indigo-100"
				title="Upload Files"
				@click="onUploadFile"
			>
				<svg
					width="18"
					height="18"
					class="s-text-current"
					fill="none"
					stroke-linecap="round"
					stroke-linejoin="round"
					stroke-width="2"
					viewBox="0 0 24 24"
					stroke="currentColor"
				>
					<path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
				</svg>
			</button>
			<!-- Download as Zip -->
			<button
				v-if="enableDownloadAsZip && hasSelectedItems"
				class="s-flex s-justify-center s-items-center s-px-3 s-appearance-none focus:s-outline-none s-text-gray-900 s-border-l s-border-700 hover:s-bg-indigo-100"
				title="Download as Zip"
				@click="onDownloadAsZip"
			>
				<svg
					width="18"
					height="18"
					class="s-text-current"
					fill="none"
					stroke-linecap="round"
					stroke-linejoin="round"
					stroke-width="2"
					viewBox="0 0 24 24"
					stroke="currentColor"
				>
					<path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
				</svg>
			</button>
			<!-- Delete Selected -->
			<button
				v-if="canDeleteSelected"
				class="s-flex s-justify-center s-items-center s-px-3 s-appearance-none focus:s-outline-none s-text-red-600 s-border-l s-border-700 hover:s-bg-indigo-100"
				:title="$t('core.deleteSelected')"
				:aria-label="`${$t('core.deleteSelected')} (${selectedCount})`"
				@click="onDeleteSelected"
			>
				<svg width="18" height="18" viewBox="0 0 24 24" class="s-text-current">
					<path fill="currentColor" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path>
					<path d="M0 0h24v24H0z" fill="none"></path>
				</svg>
				<span class="s-ml-1 s-text-sm s-font-medium">{{ selectedCount }}</span>
			</button>
		</div>
	</JFlex>
</template>

<script>
export default {
	props: {
		bucketName: {
			type: String,
			required: true
		},
		path: {
			type: Array,
			required: true,
			default: () => []
		},
		hasCutItem: {
			type: Boolean,
			required: true,
			default: () => false
		},
		isSearching: {
			type: Boolean,
			required: true,
			default: () => false
		},
		enableDownloadAsZip: {
			type: Boolean,
			default: false
		},
		hasSelectedItems: {
			type: Boolean,
			default: false
		},
		canDeleteSelected: {
			type: Boolean,
			default: false
		},
		selectedCount: {
			type: Number,
			default: 0
		}
	},

	methods: {
		onPathClick(path) {
			this.$emit("path:clicked", path)
		},
		onRefresh() {
			this.$emit("action:refresh")
		},
		onSearch() {
			this.$emit("action:search")
		},
		onCreateFolder() {
			this.$emit("action:createFolder")
		},
		onUploadFile() {
			this.$emit("action:uploadFile")
		},
		onPaste() {
			this.$emit("action:pasteFile")
		},
		onDownloadAsZip() {
			this.$emit("action:downloadAsZip")
		},
		onDeleteSelected() {
			this.$emit("action:deleteSelected")
		}
	}
}
</script>
